import React, { useState, useEffect } from 'react';
import { Col, Container, Row, Form } from 'react-bootstrap';
import { Formik } from 'formik';
import { usePasswordSettings } from './hooks';
import Button from '../common/button';
import { toast } from 'react-toastify';
import { Loader } from 'semantic-ui-react';
import SelectComponent from '../common/AutoSuggestSelect/Select';
import OTPSettingsSkeleton from './Skeletons/OTPSettingsSkeleton';
import ToastMessage from '../common/ToastMessage';
import GenericButton from 'src/Components/GenericButton/GenericButton';

import 'react-toastify/dist/ReactToastify.css';

const OTPSettings = () => {
  const {
    OTPschema,
    OTPformValues,
    updatedOTPFormValues,
    loader,
    skeletonLoader,
    enableOTP,
    handleCheckBox,
    handleInput,
    handleSelect,
    savePasswordSettings,
  } = usePasswordSettings('otpSetting');

  const [valuesChanged, setValueChanged] = useState(false);
  const SuccessMessage = () => (
    <ToastMessage
      type="success"
      title="OTP Settings"
      subTitle="Password Settings has been saved successfully."
    />
  );

  const ErrorMessage = () => (
    <ToastMessage
      type="error"
      title="OTP Settings"
      subTitle=" Error occurred while saving OTP Settings."
    />
  );

  return (
    <Container className="otp-setting-container">
      <Row>
        <Col sm={12} className="p-0">
          <h2 className="pb-3 mb-4">One Time Password Setting</h2>
        </Col>
        <Col sm={12} className="p-0">
          <p className="mb-4">
            One time password would be sent to all email addresses.
          </p>
          <Formik
            validationSchema={OTPschema}
            initialValues={{
              TwoFARequired: OTPformValues.TwoFARequired,
              OTPExpiresIn: OTPformValues.OTPExpiresIn,
              InvalidOTPAttempts: OTPformValues.InvalidOTPAttempts,
              OTPLength: OTPformValues.OTPLength,
              FrequencyForTrustedDevice:
                OTPformValues.FrequencyForTrustedDevice,
              FrequencyTypeForTrustedDevice:
                OTPformValues.FrequencyTypeForTrustedDevice,
            }}
            enableReinitialize
            onSubmit={(values, actions) => {
              actions.setStatus(undefined);
              savePasswordSettings(values)
                .then(() => {
                  toast.success(<SuccessMessage />, setValueChanged(false));
                })
                .catch((error) => {
                  toast.error(<ErrorMessage />, setValueChanged(false));
                });
            }}
          >
            {({
              handleSubmit,
              handleChange,
              handleBlur,
              values,
              touched,
              errors,
              isValid,
              setFieldValue,
            }) =>
              skeletonLoader ? (
                <OTPSettingsSkeleton />
              ) : (
                <>
                  <Form onSubmit={handleSubmit}>
                    <Row>
                      <Col>
                        <Form.Group controlId="TwoFARequired" className="mb-4">
                          <Form.Check
                            type="checkbox"
                            label="Enable One Time Password"
                            checked={updatedOTPFormValues.TwoFARequired}
                            onChange={(e) => {
                              handleCheckBox(e);
                              setValueChanged(true);
                            }}
                            name="TwoFARequired"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    {Boolean(enableOTP) && (
                      <>
                        <Row>
                          <Col sm={4}>
                            <Form.Group
                              controlId="OTPExpiresIn"
                              className="mb-4"
                            >
                              <Form.Label for="MinLength" className="m-0">
                                Duration for OTP Validation
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={updatedOTPFormValues.OTPExpiresIn}
                                onChange={(e) => {
                                  handleInput(e, setFieldValue, handleChange);
                                  setValueChanged(true);
                                }}
                                onBlur={(e) => {
                                  handleInput(e, setFieldValue, handleBlur);
                                }}
                                name="OTPExpiresIn"
                                isInvalid={
                                  touched.OTPExpiresIn && errors.OTPExpiresIn
                                }
                              />
                              <Form.Control.Feedback
                                type="invalid"
                                className="mt-1"
                              >
                                <span role="alert">
                                  {touched.OTPExpiresIn && errors.OTPExpiresIn}
                                </span>
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col
                            sm={4}
                            className="col-sm-6 d-flex align-items-center p-0"
                          >
                            <p>Minutes</p>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={4}>
                            <Form.Group
                              controlId="InvalidOTPAttempts"
                              className="mb-4"
                            >
                              <Form.Label
                                for="InvalidOTPAttempts"
                                className="m-0"
                              >
                                Maximum Number of Attempt(s)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={updatedOTPFormValues.InvalidOTPAttempts}
                                onChange={(e) => {
                                  handleInput(e, setFieldValue, handleChange);
                                  setValueChanged(true);
                                }}
                                onBlur={(e) =>
                                  handleInput(e, setFieldValue, handleBlur)
                                }
                                name="InvalidOTPAttempts"
                                isInvalid={
                                  touched.InvalidOTPAttempts &&
                                  errors.InvalidOTPAttempts
                                }
                              />
                              <Form.Control.Feedback
                                type="invalid"
                                className="mt-1"
                              >
                                <span role="alert">
                                  {touched.InvalidOTPAttempts &&
                                    errors.InvalidOTPAttempts}
                                </span>
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={4}>
                            <Form.Group controlId="OTPLength" className="mb-4">
                              <Form.Label for="OTPLength" className="m-0">
                                OTP Length
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={updatedOTPFormValues.OTPLength}
                                onChange={(e) => {
                                  handleInput(e, setFieldValue, handleChange);
                                  setValueChanged(true);
                                }}
                                onBlur={(e) =>
                                  handleInput(e, setFieldValue, handleBlur)
                                }
                                name="OTPLength"
                                isInvalid={
                                  touched.OTPLength && errors.OTPLength
                                }
                              />
                              <Form.Control.Feedback
                                type="invalid"
                                className="mt-1"
                              >
                                <span role="alert">
                                  {touched.OTPLength && errors.OTPLength}
                                </span>
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                        </Row>

                        <Row>
                          <Col sm={4}>
                            <Form.Group
                              controlId="FrequencyForTrustedDevice"
                              className="mb-5"
                            >
                              <Form.Label
                                for="FrequencyForTrustedDevice"
                                className="m-0"
                              >
                                Interval for each OTP (Only for Trusted device)
                              </Form.Label>
                              <Form.Control
                                type="text"
                                value={
                                  updatedOTPFormValues.FrequencyForTrustedDevice
                                }
                                onChange={(e) => {
                                  handleInput(e, setFieldValue, handleChange);
                                  setValueChanged(true);
                                }}
                                onBlur={(e) =>
                                  handleInput(e, setFieldValue, handleBlur)
                                }
                                name="FrequencyForTrustedDevice"
                                isInvalid={
                                  touched.FrequencyForTrustedDevice &&
                                  errors.FrequencyForTrustedDevice
                                }
                              />
                              <Form.Control.Feedback
                                type="invalid"
                                className="mt-1"
                              >
                                <span role="alert">
                                  {touched.FrequencyForTrustedDevice &&
                                    errors.FrequencyForTrustedDevice}
                                </span>
                              </Form.Control.Feedback>
                            </Form.Group>
                          </Col>
                          <Col sm={4}>
                            <Form.Group
                              controlId="FrequencyTypeForTrustedDevice"
                              className="mb-5"
                            >
                              <Form.Label
                                for="FrequencyForTrustedDevice"
                                className="invisible mb-0"
                              >
                                Type
                              </Form.Label>

                              <SelectComponent
                                options={[
                                  { label: 'Days', value: 'Days' },
                                  { label: 'Logins', value: 'Logins' },
                                ]}
                                name="FrequencyTypeForTrustedDevice"
                                onChange={(e) => {
                                  handleSelect(
                                    e,
                                    'FrequencyTypeForTrustedDevice',
                                  );
                                  setValueChanged(true);
                                }}
                                components={{ IndicatorSeparator: () => null }}
                                inputClassName="best-match-filter height-32"
                                value={{
                                  label:
                                    updatedOTPFormValues.FrequencyTypeForTrustedDevice,
                                  value:
                                    updatedOTPFormValues.FrequencyTypeForTrustedDevice,
                                }}
                                isSearchable={false}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                      </>
                    )}

                    <Row className="mb-4">
                      <Col>
                        <GenericButton
                          disable={!valuesChanged || loader}
                          buttonDisabled={!valuesChanged}
                          type="submit"
                        >
                          Save
                        </GenericButton>
                      </Col>
                    </Row>

                    {/*{loader && (
                      <div className="custom-loader mt-0 mt-md-3 mt-lg-4 d-flex justify-content-start">
                        <div className="loader-icon position-relative mr-4">
                          <Loader
                            className="text-dark font-weight-bold loader-color marginTop-Login-loader"
                            size="small"
                            active
                          />
                        </div>
                        <div className="text-dark font-weight-bold">Saving settings...</div>
                      </div>
                    )} */}
                  </Form>
                </>
              )
            }
          </Formik>
        </Col>
      </Row>
    </Container>
  );
};

export default OTPSettings;
